import type { CMSAssetDashboardActivity, CMSDashboardStatsTableColumn } from '../../types';
import type { SortDescriptor, TableProps } from '@workramp/ui';
import { Icon, Table } from '@workramp/ui';
import { useState } from 'react';
import { useTheme } from 'styled-components';
import { useNavigate } from 'react-router';
import { I18n } from '~/common/explicit-exports';
import { DateTimeCell } from '~/features/tables';
import { CMSDashboardEmptyTable } from '../CMSDashboardEmptyTable';
import { useGetDashboardActivity } from '../../api';
import { getCMSAssetLink } from '../../utils';
import { StyledButton } from './CMSDashboardActivityTable.styles';

const { t } = I18n;
const DEFAULT_ROWS_PER_PAGE = 50;
const DEFAULT_CURRENT_PAGE = 1;
const DEFAULT_PAGINATION_ITEMS_PER_PAGE = 25;
const DEFAULT_PAGINATION_ITEMS_PER_PAGE_OPTIONS = [25, 50, 100];
const { APP_CONSTANTS } = window;

enum ColumnKeys {
  lastActivityDate = 'lastActivityDate',
  name = 'title',
  views = 'totalViews',
  downloads = 'totalDownloads',
  shares = 'totalShares',
}

const getSortDirection = (sortDirection: string) => (sortDirection === 'ascending' ? 'asc' : 'desc');

const TRANSLATED_TITLE = t('js.admin.cms.dashboard_table.columns.title');
const TRANSLATED_LAST_ACTIVITY = t('js.admin.cms.dashboard_table.columns.last_activity');
const TRANSLATED_VIEWS = t('js.admin.cms.dashboard_table.columns.views');
const TRANSLATED_DOWNLOADS = t('js.admin.cms.dashboard_table.columns.downloads');
const TRANSLATED_SHARES = t('js.admin.cms.dashboard_table.columns.shares');

export const columns: CMSDashboardStatsTableColumn[] = [
  { name: TRANSLATED_TITLE, key: ColumnKeys.name, allowSorting: true },
  { name: TRANSLATED_LAST_ACTIVITY, key: ColumnKeys.lastActivityDate, allowSorting: true },
  { name: TRANSLATED_VIEWS, key: ColumnKeys.views, allowSorting: true },
  { name: TRANSLATED_DOWNLOADS, key: ColumnKeys.downloads, allowSorting: true },
  { name: TRANSLATED_SHARES, key: ColumnKeys.shares, allowSorting: true },
];

export const CMSDashboardActivityTable = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(DEFAULT_CURRENT_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: ColumnKeys.lastActivityDate,
    direction: 'descending',
  });

  const getDashboardActivity = useGetDashboardActivity(
    sortDescriptor.column as ColumnKeys,
    getSortDirection(sortDescriptor.direction),
    rowsPerPage,
    currentPage,
  );

  let tableProps: TableProps<never> = {
    isLoading: getDashboardActivity.isLoading,
    showLastRowDivider: true,
    rowPadding: theme.spacing(8),
    emptyState: <CMSDashboardEmptyTable />,
    onSortChange: setSortDescriptor,
    sortDescriptor,
  };

  if (getDashboardActivity.data?.pagination?.totalCount > DEFAULT_PAGINATION_ITEMS_PER_PAGE) {
    tableProps = {
      ...tableProps,
      page: currentPage,
      allowsItemsPerPage: true,
      itemsPerPage: rowsPerPage,
      itemsPerPageOptions: DEFAULT_PAGINATION_ITEMS_PER_PAGE_OPTIONS,
      onPageChange: setCurrentPage,
      onItemsPerPageChange: setRowsPerPage,
      totalItems: getDashboardActivity.data?.pagination.totalCount,
    };
  }

  return (
    <Table {...tableProps}>
      <Table.Header>
        {columns.map((column) => (
          <Table.Column key={column.key} allowsSorting={column.allowSorting}>
            {' '}
            {column.name}{' '}
          </Table.Column>
        ))}
      </Table.Header>
      <Table.Body>
        {getDashboardActivity.data?.data?.map((row: CMSAssetDashboardActivity) => {
          const { cmsAssetId, title, assetType, lastActivityDate, totalViews, totalDownloads, totalShares } = row;
          return (
            <Table.Row key={cmsAssetId}>
              <Table.Cell>
                <StyledButton
                  aria-label={`button--${title}`}
                  leadingIcon={
                    assetType === APP_CONSTANTS.CMS_ASSET_TYPES.LINK ? (
                      <Icon name="link:regular" color={theme.colors.decorative.neutral[900]} />
                    ) : (
                      <Icon name="file-document:regular" color={theme.colors.decorative.neutral[900]} />
                    )
                  }
                  onPress={() => navigate(getCMSAssetLink(assetType, cmsAssetId))}
                  variant="text"
                  tooltip={title}
                >
                  {title}
                </StyledButton>
              </Table.Cell>
              <Table.Cell>
                <DateTimeCell isLocalTime date={new Date(lastActivityDate).valueOf()} />
              </Table.Cell>
              <Table.Cell>{totalViews}</Table.Cell>
              <Table.Cell>{totalDownloads}</Table.Cell>
              <Table.Cell>{totalShares}</Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
};
